import React, { useState, useEffect } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Label,
  Input,
} from "reactstrap"
import API from "../../api/axios"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

const DoctorTimetable = ({ refresh }) => {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredDoctors, setFilteredDoctors] = useState([])

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ]

  const formik = useFormik({
    initialValues: {
      user_id: "",
      // speciality: "",
      availability: days.reduce((acc, day) => {
        acc[day] = { enabled: false, start_time: "", end_time: "" }
        return acc
      }, {}),
    },

    validationSchema: Yup.object({
      user_id: Yup.string().required("Doctor is required"),
      // speciality: Yup.string().required("Speciality is required"),
      availability: Yup.object().shape(
        days.reduce((acc, day) => {
          acc[day] = Yup.object({
            enabled: Yup.boolean(),
            start_time: Yup.string().when("enabled", {
              is: true,
              then: schema => schema.required("Start time is required"),
              otherwise: schema => schema.notRequired(),
            }),
            end_time: Yup.string().when("enabled", {
              is: true,
              then: schema =>
                schema
                  .required("End time is required")
                  .test(
                    "is-after-start",
                    "End time must be after start time",
                    function (value) {
                      const { start_time, enabled } = this.parent
                      if (!enabled || !start_time || !value) return true

                      // Convert to Date objects using today's date
                      const [startHour, startMinute] = start_time
                        .split(":")
                        .map(Number)
                      const [endHour, endMinute] = value.split(":").map(Number)

                      const start = new Date()
                      start.setHours(startHour, startMinute, 0)

                      const end = new Date()
                      end.setHours(endHour, endMinute, 0)

                      return end > start
                    },
                  ),
              otherwise: schema => schema.notRequired(),
            }),
          })
          return acc
        }, {}),
      ),
    }),

    onSubmit: async values => {
      console.log("Formik values before submitting:", values)

      const requestPayload = {
        userId: values.user_id,
      }

      const dayMappings = {
        monday: "Mon",
        tuesday: "Tues",
        wednesday: "Wed",
        thursday: "Thurs",
        friday: "Fri",
        saturday: "Sat",
      }

      days.forEach(day => {
        const shortDay = dayMappings[day]
        requestPayload[`is${shortDay}`] = values.availability[day].enabled

        if (values.availability[day].enabled) {
          requestPayload[`${shortDay.toLowerCase()}Starttime`] =
            values.availability[day].start_time || ""
          requestPayload[`${shortDay.toLowerCase()}Endtime`] =
            values.availability[day].end_time || ""
        } else {
          requestPayload[`${shortDay.toLowerCase()}Starttime`] = null
          requestPayload[`${shortDay.toLowerCase()}Endtime`] = null
        }
      })

      console.log(" Fixed Request payload:", requestPayload)

      try {
        const token = localStorage.getItem("authToken")

        const response = await API.post(
          "/doctorAvailibility",
          requestPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        console.log(" Doctor availability response:", response.data)

        toast.success("Doctor availability added!", { autoClose: 2000 })
        refresh()
        handleClose()
      } catch (error) {
        console.error(" Error:", error)
        toast.error("Failed to add availability", { autoClose: 2000 })
      }
    },
  })

  const handleSearch = async value => {
    try {
      const token = localStorage.getItem("authToken")
      const res = await API.get(
        `/doctorAvailibility/demo/search?search=${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      setFilteredDoctors(res.data.users || [])
    } catch (err) {
      console.error(err)
      toast.error("Error searching doctors", { autoClose: 2000 })
    }
  }

  useEffect(() => {
    if (searchTerm.length > 2) {
      handleSearch(searchTerm)
    } else {
      setFilteredDoctors([])
    }
  }, [searchTerm])

  const handleUserClick = doctor => {
    formik.setFieldValue("user_id", doctor.id)
    // formik.setFieldValue("speciality", doctor.speciality)
    setSearchTerm(`${doctor.fname} ${doctor.lname}`)
    setFilteredDoctors([])
  }

  const handleOpen = () => {
    formik.resetForm()
    setSearchTerm("")
    setOpen(true)
  }

  const handleClose = () => setOpen(false)
  useEffect(() => {}, [formik.errors])

  useEffect(() => {
    console.log("Validation errors:", formik.errors)
  }, [formik.errors])

  return (
    <>
      <Modal isOpen={open} toggle={handleClose}>
        <ModalHeader toggle={handleClose}>Doctor Sitting Time</ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md="12">
                <Label>Search Doctor</Label>
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search doctor name"
                />
                {filteredDoctors.length > 0 && (
                  <ul className="dropdown-menu show w-100 shadow bg-light">
                    {filteredDoctors.map(doc => (
                      <li key={doc.id}>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => handleUserClick(doc)}
                        >
                          {doc.fname} {doc.lname}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Col>
            </Row>

            {days.map(day => (
              <Row className="mt-3" key={day}>
                <Col
                  md="12"
                  className="d-flex justify-content-between align-items-center"
                >
                  <Label className="text-capitalize mb-0">{day}</Label>
                  <div className="form-check form-switch">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      name={`availability.${day}.enabled`}
                      checked={formik.values.availability[day].enabled}
                      onChange={e =>
                        formik.setFieldValue(
                          `availability.${day}.enabled`,
                          e.target.checked,
                        )
                      }
                    />
                    <Label className="form-check-label ms-2">
                      <i className="bi bi-person-check me-1" /> Doctor Sitting
                    </Label>
                  </div>
                </Col>
                <Col md="6" className="mt-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    name={`availability.${day}.start_time`}
                    value={formik.values.availability[day].start_time}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.availability[day].enabled}
                    invalid={
                      !!formik.touched.availability?.[day]?.start_time &&
                      !!formik.errors.availability?.[day]?.start_time
                    }
                  />
                  {formik.touched.availability?.[day]?.start_time &&
                    formik.errors.availability?.[day]?.start_time && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formik.errors.availability[day].start_time}
                      </div>
                    )}
                </Col>

                <Col md="6" className="mt-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    name={`availability.${day}.end_time`}
                    value={formik.values.availability[day].end_time}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.availability[day].enabled}
                    invalid={
                      !!formik.touched.availability?.[day]?.end_time &&
                      !!formik.errors.availability?.[day]?.end_time
                    }
                  />
                  {formik.touched.availability?.[day]?.end_time &&
                    formik.errors.availability?.[day]?.end_time && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formik.errors.availability[day].end_time}
                      </div>
                    )}
                </Col>
              </Row>
            ))}

            <ModalFooter className="mt-4">
              <Button color="primary" type="submit">
                Submit
              </Button>
              <Button color="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}

export default DoctorTimetable
